import React from 'react';
import { OfferDetailsData } from '@/pages/hettwer/types/OfferDetailsData';
import { formatDateOnly } from '@/pages/hettwer/utils/Date';
import { KeenIcon } from '@/components';
import { toAbsoluteUrl } from '@/utils';
import { getSensitiveFieldClassName, usePrivacyMode } from '@/utils/privacyMode';

interface OfferCustomerViewProps {
  offer: OfferDetailsData;
  onClose: () => void;
}

const formatMoney = (value: number | null | undefined) =>
  typeof value === 'number' ? `${value.toFixed(2)} €` : null;

const OfferCustomerView: React.FC<OfferCustomerViewProps> = ({ offer, onClose }) => {
  const { isPrivacyMode } = usePrivacyMode();

  const hasCustomerBlock =
    offer.customer?.name ||
    offer.customer?.contact_person ||
    offer.customer?.email ||
    offer.customer?.phone ||
    offer.customer?.address;

  const hasSummaryBlock =
    offer.summary?.subject || offer.summary?.project_type || offer.summary?.description;

  return (
    <div className="fixed inset-0 z-[9999] bg-white text-black overflow-auto">
      <div className="fixed top-6 right-6 z-[10000]">
        <button onClick={onClose} className="btn btn-sm btn-light">
          <KeenIcon icon="cross" />
          Schließen
        </button>
      </div>

      <div className="max-w-[1100px] mx-auto px-16 py-12">
        <div className="flex justify-between items-start mb-12">
          <div>
            <img
              src={toAbsoluteUrl('/media/images/logo.svgz')}
              alt="Hettwer Metallbau"
              className="h-14 mb-6"
            />

            {offer.customer?.name && (
              <div
                className={`text-sm leading-relaxed ${getSensitiveFieldClassName(isPrivacyMode)}`}
              >
                <strong>{offer.customer.name}</strong>
                {offer.customer.address && (
                  <>
                    <br />
                    {offer.customer.address}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="text-sm text-right leading-relaxed">
            {offer.offer_date && <div>Datum: {formatDateOnly(offer.offer_date)}</div>}
            {offer.valid_until && <div>Gültig bis: {formatDateOnly(offer.valid_until)}</div>}
            {offer.offer_number && (
              <div className="mt-2">
                Angebotsnummer: <strong>{offer.offer_number}</strong>
              </div>
            )}
          </div>
        </div>

        {hasCustomerBlock && (
          <div className="mb-8 text-sm">
            <table>
              <tbody>
                {offer.customer?.contact_person && (
                  <tr>
                    <td className="pr-6 py-1 text-gray-600">Ansprechpartner</td>
                    <td className={getSensitiveFieldClassName(isPrivacyMode)}>
                      {offer.customer.contact_person}
                    </td>
                  </tr>
                )}
                {offer.customer?.email && (
                  <tr>
                    <td className="pr-6 py-1 text-gray-600">E-Mail</td>
                    <td className={getSensitiveFieldClassName(isPrivacyMode)}>
                      {offer.customer.email}
                    </td>
                  </tr>
                )}
                {offer.customer?.phone && (
                  <tr>
                    <td className="pr-6 py-1 text-gray-600">Telefon</td>
                    <td className={getSensitiveFieldClassName(isPrivacyMode)}>
                      {offer.customer.phone}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {offer.offer_number && (
          <h1 className="text-2xl font-semibold mb-4">Angebot {offer.offer_number}</h1>
        )}

        {hasSummaryBlock && (
          <div className="mb-8 text-sm space-y-3">
            {offer.summary?.subject && (
              <div className="text-base font-medium">{offer.summary.subject}</div>
            )}

            {offer.summary?.project_type && (
              <div>
                <span className="text-gray-600">Projektart: </span>
                {offer.summary.project_type}
              </div>
            )}

            {offer.summary?.description && (
              <div className="whitespace-pre-line">{offer.summary.description}</div>
            )}
          </div>
        )}

        <table className="w-full border-collapse text-sm mb-10">
          <thead>
            <tr className="border-b border-gray-400">
              <th className="text-left py-2 w-16">Pos.</th>
              <th className="text-left py-2">Leistung</th>
              <th className="text-right py-2 w-32">Preis</th>
            </tr>
          </thead>
          <tbody>
            {offer.positions.map((pos, index) => (
              <tr key={index} className="align-top border-b border-gray-200">
                <td className="py-3">{pos.position_no ?? index + 1}</td>
                <td className="py-3 whitespace-pre-line">
                  <strong>{pos.title}</strong>
                  {pos.description && `\n${pos.description}`}
                </td>
                <td className="py-3 text-right">{formatMoney(pos.pricing?.total_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end">
          <table className="text-sm">
            <tbody>
              {formatMoney(offer.totals?.net_total) && (
                <tr>
                  <td className="pr-8 py-1">Netto</td>
                  <td className="text-right py-1">{formatMoney(offer.totals?.net_total)}</td>
                </tr>
              )}
              {formatMoney(offer.totals?.vat_total) && (
                <tr>
                  <td className="pr-8 py-1">MwSt. ({offer.totals?.vat_rate ?? 19}%)</td>
                  <td className="text-right py-1">{formatMoney(offer.totals?.vat_total)}</td>
                </tr>
              )}
              {formatMoney(offer.totals?.gross_total) && (
                <tr className="font-semibold border-t border-gray-400">
                  <td className="pr-8 py-2">Gesamtsumme</td>
                  <td className="text-right py-2">{formatMoney(offer.totals?.gross_total)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-16 text-sm">
          Fachmännische und sorgfältige Ausführung sichern wir Ihnen zu und würden uns freuen, wenn
          Sie uns mit der Ausführung Ihrer Arbeiten beauftragen.
          <br />
          <br />
          Mit freundlichen Grüßen
          <br />
          <strong>Hettwer Metallbau GmbH</strong>
        </div>
      </div>
    </div>
  );
};

export { OfferCustomerView };
